export type DiffLineType = 'added' | 'removed' | 'unchanged';

export interface DiffLine {
  type: DiffLineType;
  /** Textual marker so the diff never conveys meaning through colour alone. */
  marker: '+' | '-' | ' ';
  text: string;
}

const MARKERS: Record<DiffLineType, DiffLine['marker']> = {
  added: '+',
  removed: '-',
  unchanged: ' ',
};

function line(type: DiffLineType, text: string): DiffLine {
  return { type, marker: MARKERS[type], text };
}

/**
 * Line-level diff over the longest common subsequence of both texts. Kept in
 * the repo rather than pulled from npm: the prompt studio only needs added /
 * removed / unchanged lines, and a diff library is a large surface for that.
 */
export function diffLines(before: string, after: string): DiffLine[] {
  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');

  // lcs[i][j] = length of the longest common subsequence of the suffixes
  // beforeLines[i..] and afterLines[j..].
  const lcs: number[][] = Array.from({ length: beforeLines.length + 1 }, () =>
    new Array<number>(afterLines.length + 1).fill(0),
  );

  for (let i = beforeLines.length - 1; i >= 0; i -= 1) {
    for (let j = afterLines.length - 1; j >= 0; j -= 1) {
      lcs[i][j] =
        beforeLines[i] === afterLines[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }

  const result: DiffLine[] = [];
  let i = 0;
  let j = 0;

  while (i < beforeLines.length && j < afterLines.length) {
    if (beforeLines[i] === afterLines[j]) {
      result.push(line('unchanged', beforeLines[i]));
      i += 1;
      j += 1;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      result.push(line('removed', beforeLines[i]));
      i += 1;
    } else {
      result.push(line('added', afterLines[j]));
      j += 1;
    }
  }

  while (i < beforeLines.length) {
    result.push(line('removed', beforeLines[i]));
    i += 1;
  }

  while (j < afterLines.length) {
    result.push(line('added', afterLines[j]));
    j += 1;
  }

  return result;
}
